import React from 'react';

import { tintFor } from '../../config/colors';

/**
 * Avatar for someone with no picture and no name to make initials from.
 *
 * The mark is a REDACTED PORTRAIT — a coarse mosaic in the shape of a head and
 * shoulders, the visual language of a pixelated-out face. It is deliberately not
 * a person silhouette:
 *
 * - A silhouette is a picture of a person, so it says "here is somebody" in a
 *   slot whose entire meaning is "we do not know who this is". Every app ships
 *   the same generic figure; none of them read as *withheld*.
 * - A mosaic says withheld at a glance, and it is the one identity-hiding
 *   convention that survives being shrunk. At the 20px this renders at, a mask
 *   or a cipher glyph is three grey pixels; four solid blocks are still four
 *   solid blocks.
 * - It also happens to be honest about the two states below: obscuring is what a
 *   secret group does on purpose, and what an unknown account is by accident.
 *
 * Blocks carry graded opacity rather than a flat fill, which is what makes it
 * read as an image that has been destroyed rather than as a chart or a QR code.
 *
 * Two intents, told apart by surface — never by a second drawing, because two
 * drawings at 20px are one drawing:
 *
 *   unknown   — no data. Neutral surface, muted mark; it recedes.
 *   anonymous — a secret group's pseudonymous member. Anonymity by design, so it
 *               takes a tint and reads as a state, not a gap.
 *
 * `seed` matters for the anonymous case: two pseudonymous members have to be
 * TELLABLE APART without being identifiable, so the mark stays identical and the
 * surface takes a per-pseudonym tint. It comes from `tintFor` — the curated
 * avatar palette, every entry of which is legible in both themes — replacing
 * `generateUniqueColor`, which hashed straight into arbitrary RGB and so produced
 * whatever contrast it happened to land on.
 *
 * This replaces `randoming.png`, the hooded-figure raster that used to stand in
 * for a secret group's members: a fixed dark bitmap that could not follow the
 * theme, sat on that arbitrary hash colour, and read as "hacker" rather than as
 * "this person's identity is withheld".
 */

/* 24×24 grid, 5px pitch (4px block + 1px gap). Two rows of two for the head,
   one wider row for the shoulders — the fewest blocks that still resolve into a
   bust rather than a pattern. Opacity varies per block so the mosaic looks like
   destroyed detail; the values are hand-picked, not sequential, because an even
   ramp reads as a gradient. */
const BLOCKS = [
  { x: 7, y: 3.5, o: 0.55 },
  { x: 12, y: 3.5, o: 0.85 },
  { x: 7, y: 8.5, o: 1 },
  { x: 12, y: 8.5, o: 0.62 },
  { x: 2, y: 15.5, o: 0.4 },
  { x: 7, y: 15.5, o: 0.9 },
  { x: 12, y: 15.5, o: 0.72 },
  { x: 17, y: 15.5, o: 0.45 },
];

const PlaceholderAvatar = ({
  size = 40,
  // Circle by default, and every call site takes the default: the photo it stands
  // in for is always a circle (`.msg-head-avatar`, `.avatar-circle`), so a
  // rounded square here made the placeholder the odd one out in a list.
  radius = 'var(--r-avatar)',
  variant = 'unknown',
  seed,
  label,
  style,
}) => {
  const anonymous = variant === 'anonymous';
  // Tinted only when there is something to tint BY. Without a seed every
  // pseudonym would share one colour, which is worse than no colour at all: it
  // implies the members are the same person.
  const tinted = anonymous && seed !== undefined && seed !== null && seed !== '';

  return (
    <span
      className={`avatar-placeholder${anonymous ? ' is-anonymous' : ''}${tinted ? ' is-tinted' : ''}`}
      role="img"
      aria-label={label ?? (anonymous ? 'Anonymous member' : 'Unknown user')}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        ...(tinted ? { background: tintFor(seed) } : null),
        ...style,
      }}
    >
      {/* Sized as a fraction of the avatar, so the mark keeps the same optical
          weight whether it is a 20px row avatar or a 140px call screen. */}
      <svg
        width={Math.round(size * 0.6)}
        height={Math.round(size * 0.6)}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        {BLOCKS.map((block) => (
          <rect
            key={`${block.x}-${block.y}`}
            x={block.x}
            y={block.y}
            width="4"
            height="4"
            rx="0.9"
            opacity={block.o}
          />
        ))}
      </svg>
    </span>
  );
};

export default PlaceholderAvatar;
